import { Chip, Paper, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { useContext, useEffect, useRef, useState } from "react";
import { isArray, isString } from "lodash";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

export function ContactTagsContainer({ contact }) {
    const [tags, setTags] = useState([]);
    const [selecteds, setSelecteds] = useState([]);
    const isMounted = useRef(true);
    const { user } = useContext(AuthContext);
    const socketManager = useContext(SocketContext);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        }
    }, [])

    useEffect(() => {
        let cancelled = false;

        if (isMounted.current) {
            loadTags().then(() => {
                if (cancelled || !isMounted.current) return;

                if (Array.isArray(contact?.tags)) {
                    setSelecteds(contact.tags);
                } else {
                    setSelecteds([]);
                }
            });
        }

        return () => {
            cancelled = true;
        };
    }, [contact]);

    // As tags também podem chegar sozinhas (frases cadastradas que marcam o contato
    // quando o cliente escreve). Sem isto a tag só apareceria ao reabrir o contato.
    useEffect(() => {
        const companyId = user?.companyId;
        if (!companyId || !contact?.id) return undefined;

        const socket = socketManager.getSocket(companyId);

        const handleContactUpdate = (data) => {
            if (data.action !== "update") return;
            if (data.contact?.id !== contact.id) return;
            if (!isMounted.current) return;
            setSelecteds(Array.isArray(data.contact.tags) ? data.contact.tags : []);
        };

        socket.on(`company-${companyId}-contact`, handleContactUpdate);

        return () => {
            socket.off(`company-${companyId}-contact`, handleContactUpdate);
        };
    }, [socketManager, user, contact]);

    const createTag = async (data) => {
        try {
            const { data: responseData } = await api.post(`/tags`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    const loadTags = async () => {
        try {
            const { data } = await api.get(`/tags/list`);
            if (!isMounted.current) return;
            setTags(data);
        } catch (err) {
            toastError(err);
        }
    }

    const syncTags = async (data) => {
        try {
            const { data: responseData } = await api.post(`/contact-tags/sync`, data);
            return responseData;
        } catch (err) {
            toastError(err);
        }
    }

    // Tags já ativas no contato não devem reaparecer na listagem do autocomplete
    const availableTags = tags.filter(
        tag => !selecteds.some(selected => selected.id === tag.id)
    );

    const onChange = async (value, reason) => {
        let optionsChanged = []
        if (reason === 'create-option') {
            if (isArray(value)) {
                for (let item of value) {
                    if (isString(item)) {
                        const normalized = item.trim().toLowerCase();
                        // Já está no contato? Ignora a digitação duplicada
                        const alreadySelected = optionsChanged.some(
                            t => !isString(t) && t.name?.trim().toLowerCase() === normalized
                        );
                        if (alreadySelected) continue;
                        // Já existe tag com esse nome? Reaproveita em vez de criar outra
                        const existingTag = tags.find(
                            t => t.name?.trim().toLowerCase() === normalized
                        );
                        if (existingTag) {
                            optionsChanged.push(existingTag);
                            continue;
                        }
                        const newTag = await createTag({ name: item })
                        optionsChanged.push(newTag);
                    } else {
                        optionsChanged.push(item);
                    }
                }
            }
            await loadTags();
        } else {
            optionsChanged = value;
        }

        setSelecteds(optionsChanged);
        await syncTags({ contactId: contact.id, tags: optionsChanged });
    }

    return (
        <Paper style={{ padding: 12 }}>
            <Autocomplete
                multiple
                size="small"
                options={availableTags}
                value={selecteds}
                getOptionSelected={(option, value) => option.id === value.id}
                freeSolo
                onChange={(e, v, r) => onChange(v, r)}
                getOptionLabel={(option) => option.name}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <Chip
                            variant="outlined"
                            style={{
                                background: option.color || '#eee',
                                color: "#FFF",
                                marginRight: 1,
                                fontWeight: 600,
                                borderRadius: 3,
                                fontSize: "0.8em",
                                whiteSpace: "nowrap",
                            }}
                            label={option.name.toUpperCase()}
                            {...getTagProps({ index })}
                            size="small"
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="outlined"
                        placeholder={i18n.t("contactModal.form.tagsPlaceholder")}
                    />
                )}
                PaperComponent={({ children }) => (
                    <Paper style={{ width: 400, marginLeft: 12 }}>
                        {children}
                    </Paper>
                )}
            />
        </Paper>
    )
}
